import React, { useEffect, useRef, useState } from 'react';
import { sessionStore } from 'stores';

export type MessageType = 'exchange_token' | 'sign_out';

export interface TokenData {
  idToken: string;
  refreshToken: string;
  accessToken: string;
  idTokenDecoded: {
    sub: string;
    'cognito:groups': string[];
    birthdate?: string;
    gender?: string;
    'custom:street'?: string;
    iss: string;
    'custom:user_id'?: string;
    'custom:profile_complete'?: string;
    locale?: string;
    'custom:nationality'?: string;
    updated_at?: number;
    auth_time: number;
    exp: number;
    iat: number;
    jti: string;
    email?: string;
    'custom:country'?: string;
    email_verified?: boolean;
    phone_number_verified?: boolean;
    'cognito:username': string;
    given_name?: string;
    'custom:city'?: string;
    origin_jti?: string;
    aud: string;
    event_id?: string;
    'custom:post_code'?: string;
    token_use: string;
    phone_number?: string;
    family_name?: string;
  };
}

export interface OdinProviderProps {
  children: React.ReactNode;
  iframeUrl?: string;
  onTokensUpdate?: (tokenData: { data: TokenData }) => void;
  onTokensRefresh?: (tokenData: { data: TokenData }) => void;
  onSignOut?: () => void;
  onIframeLoaded?: () => void;
  onCookiesBlocked?: () => void;
}

export const OdinProvider: React.FC<OdinProviderProps> = ({
  children,
  onTokensUpdate,
  onTokensRefresh,
  onSignOut,
  onIframeLoaded,
  onCookiesBlocked,
  iframeUrl = 'http://localhost:4200/tokens',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const hasReceivedTokensRef = useRef(false);

  // Helper function to decode JWT token
  const decodeJWT = (token: string): Record<string, number | string> | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Helper function to check if token is Odin-compatible (new) or legacy
  const isOdinToken = (decodedToken: any): boolean => {
    return decodedToken && 'cognito:username' in decodedToken;
  };

  // Helper function to check JWT token expiration
  const isTokenExpired = (decodedToken: any): boolean => {
    if (!decodedToken || !decodedToken.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp < currentTime;
  };

  // Check token on mount
  useEffect(() => {
    // Check for token in URL query params (from redirect after auth)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const actionParam = urlParams.get('action');

    if (actionParam === 'sign_out') {
      // Remove action param from URL
      urlParams.delete('action');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);

      sessionStore.logout();
      return;
    }

    if (tokenParam) {
      try {
        // Decode the base64 token
        const decodedToken = atob(tokenParam);

        // Remove token from URL and history FIRST (before any async operations)
        // This preserves the current pathname, remaining query params, and hash
        urlParams.delete('token');
        const search = urlParams.toString();
        const newUrl = search
          ? `${window.location.pathname}?${search}${window.location.hash}`
          : `${window.location.pathname}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);

        // Validate the token from URL before accepting it
        const decodedJWT = decodeJWT(decodedToken);
        if (decodedJWT && isOdinToken(decodedJWT) && !isTokenExpired(decodedJWT)) {
          // Store the token in localStorage
          localStorage.setItem('token', decodedToken);
          // Pass it to onTokensUpdate
          onTokensUpdate?.({
            data: {
              idToken: decodedToken,
              refreshToken: '',
              accessToken: '',
            } as TokenData,
          });
          setIsTokenValidated(true);
        } else {
          // Token from URL is invalid or expired, redirect to auth
          console.warn('Token from URL is invalid or expired - redirecting to check-auth');
          const odinOrigin = new URL(iframeUrl).origin;
          const returnUrl = encodeURIComponent(window.location.href);
          window.location.href = `${odinOrigin}/check-auth?redirectUri=${returnUrl}`;
        }
        return;
      } catch (error) {
        console.error('Error decoding token from URL:', error);
      }
    }

    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      // 1. Decode the token
      const decodedToken = decodeJWT(storedToken);
      // If token can't be decoded or doesn't have cognito:username, it's legacy
      if (!decodedToken || !isOdinToken(decodedToken)) {
        // 2.1 Legacy token detected
        sessionStore.logout();
        // Redirect to auth to get a valid token
        const odinOrigin = new URL(iframeUrl).origin;
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${odinOrigin}/check-auth?redirectUri=${returnUrl}`;
        return;
      } else {
        // 2.2 New Odin-compatible token
        // 3. Check if token is expired
        if (isTokenExpired(decodedToken)) {
          console.warn('Token expired - redirecting to check-auth');
          const odinOrigin = new URL(iframeUrl).origin;
          const returnUrl = encodeURIComponent(window.location.href);
          window.location.href = `${odinOrigin}/check-auth?redirectUri=${returnUrl}`;
          return;
        }
        // Token is valid and not expired
        setIsTokenValidated(true);
      }
    } else {
      // No token stored - allow rendering children
      setIsTokenValidated(true);
    }
  }, [iframeUrl, onTokensUpdate]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(iframeUrl).origin) {
        return;
      }
      if (event.data.type === 'exchange_token') {
        hasReceivedTokensRef.current = true;
        // Store the token in localStorage
        if (event.data.data?.idToken) {
          localStorage.setItem('token', event.data.data.idToken);
        }
        onTokensUpdate?.(event.data);
        setIsTokenValidated(true);
      } else if (event.data.type === 'sign_out') {
        onSignOut?.();
      } else if (event.data.type === 'no_cookies') {
        // Iframe detected it can't access cookies
        console.warn('Third-party cookies blocked (reported by iframe)');
        onCookiesBlocked?.();
      } else if (event.data.type === 'refresh_token') {
        // Store the refreshed token in localStorage
        if (event.data.data?.idToken) {
          localStorage.setItem('token', event.data.data.idToken);
        }
        onTokensRefresh?.(event.data);
        setIsTokenValidated(true);
      }
    };

    const handleIframeLoad = () => {
      console.log('Odin iframe loaded');
      setIframeLoaded(true);
      onIframeLoaded?.();
    };

    window.addEventListener('message', handleMessage);
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad);
      }
    };
  }, [onTokensUpdate, onSignOut, onIframeLoaded, onCookiesBlocked, iframeUrl]);

  // Block rendering until iframe is loaded AND token is validated
  if (!iframeLoaded || !isTokenValidated) {
    return (
      <>
        <iframe
          ref={iframeRef}
          title='Odin'
          src={iframeUrl}
          style={{
            visibility: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px',
          }}
        >
          Loading...
        </div>
      </>
    );
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        title='Odin'
        src={iframeUrl}
        style={{
          visibility: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
      {children}
    </>
  );
};

export default OdinProvider;
